import React, { useState, useRef, useEffect } from "react";
import ModalContent from "@/components/Modal/ModalContent";
import Input from "@/components/common/Form/Input";
import { Common, CustomScrollbar } from "@/components";
import { Icon } from "@/components/Icons";
import { SnackbarUtils } from "@/utils/notistack";
import { useInviteDrivers } from "../services/driverService";

interface InviteDriversDialogProps {
  open: boolean;
  onClose: () => void;
  importSessionId: string;
  onSuccess?: () => void;
}

const InviteDriversDialog: React.FC<InviteDriversDialogProps> = ({
  open,
  onClose,
  importSessionId,
  onSuccess,
}) => {
  const [driverEmails, setDriverEmails] = useState<string[]>([""]);
  const [validEmails, setValidEmails] = useState<boolean[]>([true]);
  const [duplicateEmails, setDuplicateEmails] = useState<boolean[]>([false]);
  const [alreadyInvitedEmails, setAlreadyInvitedEmails] = useState<{
    [email: string]: boolean;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newRowAdded, setNewRowAdded] = useState(false);
  const lastInputRef = useRef<HTMLInputElement>(null);

  const inviteDriversMutation = useInviteDrivers();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email === "" || emailRegex.test(email);
  };

  const checkDuplicateEmail = (
    index: number,
    email: string,
    emailsArray: string[] = driverEmails
  ): boolean => {
    if (!email.trim()) return false;

    return emailsArray.some(
      (existingEmail, idx) =>
        idx !== index &&
        existingEmail.trim().toLowerCase() === email.trim().toLowerCase()
    );
  };

  const handleEmailChange = (index: number, value: string) => {
    const updatedEmails = [...driverEmails];
    updatedEmails[index] = value;
    setDriverEmails(updatedEmails);

    // Check email format validity
    const updatedValidation = [...validEmails];
    updatedValidation[index] = validateEmail(value);
    setValidEmails(updatedValidation);

    // Check for duplicate emails
    const updatedDuplicates = [...duplicateEmails];
    updatedDuplicates[index] = checkDuplicateEmail(index, value);
    setDuplicateEmails(updatedDuplicates);

    // Reset already invited status when email changes
    if (alreadyInvitedEmails[value.toLowerCase()]) {
      const updatedInvitedEmails = { ...alreadyInvitedEmails };
      delete updatedInvitedEmails[value.toLowerCase()];
      setAlreadyInvitedEmails(updatedInvitedEmails);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If it's the last row and can add a new row, add one
      if (index === driverEmails.length - 1 && canAddNewRow()) {
        addEmailInput();
      }
    }
  };

  const canAddNewRow = () => {
    const lastEmailIndex = driverEmails.length - 1;
    return (
      driverEmails[lastEmailIndex] && driverEmails[lastEmailIndex].trim() !== ""
    );
  };

  const addEmailInput = () => {
    if (canAddNewRow()) {
      setDriverEmails([...driverEmails, ""]);
      setValidEmails([...validEmails, true]);
      setDuplicateEmails([...duplicateEmails, false]);
      setNewRowAdded(true);
    }
  };

  const removeEmailInput = (index: number) => {
    // Don't allow removing the last row if it's the only one
    if (driverEmails.length === 1) {
      return;
    }

    const updatedEmails = [...driverEmails];
    updatedEmails.splice(index, 1);
    setDriverEmails(updatedEmails);

    const updatedValidation = [...validEmails];
    updatedValidation.splice(index, 1);
    setValidEmails(updatedValidation);

    const updatedDuplicates = [...duplicateEmails];
    updatedDuplicates.splice(index, 1);
    setDuplicateEmails(updatedDuplicates);

    // Check for duplicates after removal
    for (let i = 0; i < updatedEmails.length; i++) {
      updatedDuplicates[i] = checkDuplicateEmail(
        i,
        updatedEmails[i],
        updatedEmails
      );
    }
    setDuplicateEmails(updatedDuplicates);
  };

  // Effect to focus on the new input when a new row is added
  useEffect(() => {
    if (newRowAdded && lastInputRef.current) {
      lastInputRef.current.focus();
      setNewRowAdded(false);
    }
  }, [newRowAdded, driverEmails.length]);

  const handleInvite = async () => {
    if (!allEmailsValid()) return;

    setIsSubmitting(true);

    // Filter out empty emails
    const emailsToInvite = driverEmails.filter((email) => email.trim() !== "");

    try {
      const result = await inviteDriversMutation.mutateAsync({
        importSessionId,
        driverEmails: emailsToInvite,
      });

      if (result.success && result.errors.length === 0) {
        SnackbarUtils.success("Invitations sent successfully");
        // Refresh driver list in parent component if onSuccess callback is provided
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else if (result.errors.length > 0) {
        // Update the alreadyInvitedEmails state based on the error response
        const newAlreadyInvitedEmails: { [email: string]: boolean } = {};

        result.errors.forEach((email) => {
          newAlreadyInvitedEmails[email.toLowerCase()] = true;
        });

        setAlreadyInvitedEmails(newAlreadyInvitedEmails);

        // Show general error message
        SnackbarUtils.error(
          "Some emails could not be invited. Please check the form for details."
        );
      } else {
        SnackbarUtils.error(
          "Failed to invite drivers. Please try again later."
        );
      }
    } catch (error) {
      SnackbarUtils.error(
        "An unexpected error occurred. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setDriverEmails([""]);
    setValidEmails([true]);
    setDuplicateEmails([false]);
    setAlreadyInvitedEmails({});
    setIsSubmitting(false);
  };

  const isEmailAlreadyInvited = (email: string): boolean => {
    return Boolean(email.trim() && alreadyInvitedEmails[email.toLowerCase()]);
  };

  const allEmailsValid = () => {
    return (
      validEmails.every((isValid) => isValid) &&
      duplicateEmails.every((isDuplicate) => !isDuplicate) &&
      driverEmails.some((email) => email.trim() !== "") &&
      driverEmails.every((email) => !isEmailAlreadyInvited(email))
    );
  };

  return (
    <ModalContent
      open={open}
      onClose={handleClose}
      panelClassWidth="sm:max-w-[30rem] sm:w-full"
    >
      <div className="p-6">
        <h3 className="text-hd5 mb-6">Invite Drivers</h3>
        <CustomScrollbar autoHeight className="mb-6">
          <div className="space-y-3 mr-6">
            {driverEmails.map((email, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1">
                  <Input
                    ref={
                      index === driverEmails.length - 1
                        ? lastInputRef
                        : undefined
                    }
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleEmailChange(index, e.target.value)
                    }
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    isError={
                      !validEmails[index] ||
                      duplicateEmails[index] ||
                      isEmailAlreadyInvited(email)
                    }
                    errorMessage={
                      !validEmails[index]
                        ? "Please enter a valid email"
                        : duplicateEmails[index]
                        ? "This email is already in the list"
                        : isEmailAlreadyInvited(email)
                        ? "This email has already been active or invited for your account"
                        : ""
                    }
                  />
                </div>
                {index === driverEmails.length - 1 ? (
                  <Common.Button
                    variant="outline"
                    color="blue"
                    iconLeft={Icon.PlusBorder}
                    iconType="fill"
                    onClick={addEmailInput}
                    tabIndex={-1}
                  />
                ) : (
                  <Common.Button
                    variant="outline"
                    color="red"
                    iconLeft={Icon.MinusBorder}
                    iconType="fill"
                    onClick={() => removeEmailInput(index)}
                    tabIndex={-1}
                  />
                )}
              </div>
            ))}
          </div>
        </CustomScrollbar>
        <div className="flex justify-end gap-3">
          <Common.Button color="transparent" onClick={handleClose}>
            Close
          </Common.Button>
          <Common.Button
            onClick={handleInvite}
            disabled={!allEmailsValid() || isSubmitting}
            isLoading={isSubmitting}
          >
            {isSubmitting ? "Inviting..." : "Invite"}
          </Common.Button>
        </div>
      </div>
    </ModalContent>
  );
};

export default InviteDriversDialog;
